//
// Copyright 2025 DXOS.org
//

import * as Effect from 'effect/Effect';
import * as Effectable from 'effect/Effectable';
import * as Option from 'effect/Option';

import type * as QueryResult from '../../QueryResult';

/**
 * Wraps an effect that produces a {@link QueryResult.QueryResult} into a
 * {@link QueryResult.QueryResultEffect}, exposing `.run` and `.first` shorthands that execute the
 * query once. Shared by `Database.query` and `Feed.query` so both surfaces chain identically.
 */
export const makeQueryResultEffect = <T, E, R>(
  eff: Effect.Effect<QueryResult.QueryResult<T>, E, R>,
): QueryResult.QueryResultEffect<T, E, R> => {
  // Effect 4 replaced the spread-in `CommitPrototype` with `Prototype`, which takes the old
  // `commit()` body as `evaluate` and builds the Effect-shaped base itself.
  const base = Effectable.Prototype<Effect.Effect<QueryResult.QueryResult<T>, E, R>>({
    label: '@dxos/echo/QueryResultEffect',
    evaluate: () => eff,
  });

  return Object.assign(base, {
    run: Effect.flatMap(eff, (result) => Effect.promise(() => result.run())),
    first: Effect.flatMap(eff, (result) =>
      Effect.promise(async () => Option.fromNullishOr(await result.firstOrUndefined())),
    ),
  }) as QueryResult.QueryResultEffect<T, E, R>;
};
